import { useContext, useEffect, useMemo, useState } from "react";

import { calculate_cecl_from_stresstest } from "@datanac/datanac-api-toolkit/dist/insurance/stress-test/cecl";
import { calculateStressTest } from "@datanac/datanac-api-toolkit/dist/insurance/stress-test/crop/crop_stress_test";
import { useCardController } from "@datanac/datanac-mui-toolkit/dist/hooks";
import { ArrowDropDownOutlined } from "@mui/icons-material";
import CalculateOutlined from "@mui/icons-material/CalculateOutlined";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  Divider,
  Icon,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { ApiHelper, RmaApiHelper, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { ReportsIcon } from "components/Icons/Icons";
import InsuranceStressTestParametersDialog from "components/Insurance/components/InsuranceStressTestParametersDialog";
import InsuranceOptimizeDataGrid from "components/InsuranceOptimizeDataGrid";
import { getUniqueCounties } from "components/Vault/Farm/helpers";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { enqueueSnackbar } from "notistack";
import { formatDateLong } from "utility/formatters";
import { v4 as uuidv4 } from "uuid";

import { ArrowDownIcon } from "../../Icons/Icons";
import {
  calculateStressTestDisplayFieldLabel,
  STRESS_TEST_FIELD_INCOME_BEFORE_INSURANCE,
  STRESS_TEST_FIELD_INCOME_PER_ACRE_BEFORE_INSURANCE,
  STRESS_TEST_FIELD_INCOME_PER_ACRE_WITH_INSURANCE,
  STRESS_TEST_FIELD_NET_INCOME_AFTER_INSURANCE,
  STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT,
  STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT_PER_ACRE,
  STRESS_TEST_FIELD_NET_PROFIT_INSURED,
  STRESS_TEST_FIELD_PROJECTED_REVENUE,
  STRESS_TEST_FIELD_REVENUE_PER_ACRE,
} from "../../Insurance/helpers";
import { InsuranceService } from "../../Insurance/InsuranceService";

const totalValueFields = [
  STRESS_TEST_FIELD_PROJECTED_REVENUE,
  STRESS_TEST_FIELD_INCOME_BEFORE_INSURANCE,
  STRESS_TEST_FIELD_NET_INCOME_AFTER_INSURANCE,
  STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT,
];

const menuOptions = [
  STRESS_TEST_FIELD_PROJECTED_REVENUE,
  STRESS_TEST_FIELD_INCOME_BEFORE_INSURANCE,
  STRESS_TEST_FIELD_NET_INCOME_AFTER_INSURANCE,
  STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT,
  "divider",
  STRESS_TEST_FIELD_REVENUE_PER_ACRE,
  STRESS_TEST_FIELD_INCOME_PER_ACRE_BEFORE_INSURANCE,
  STRESS_TEST_FIELD_INCOME_PER_ACRE_WITH_INSURANCE,
  STRESS_TEST_FIELD_NET_INSURANCE_BENEFIT_PER_ACRE,
];

const generateStressTestParamsKey = (details) => {
  const { county_name, state_name, commodity_name, type_name, practice_name } = details;
  return `${county_name}-${state_name}-${commodity_name}-${type_name}-${practice_name}`;
};

export default function StressTestReport() {
  const currentDate = formatDateLong(new Date());
  const [isStressTestOptionsDialogOpen, setIsStressTestOptionsDialogOpen] = useState(false);
  const [stressTestDisplayField, setStressTestDisplayField] = useState(STRESS_TEST_FIELD_NET_PROFIT_INSURED);
  const [stressTestParameters, setStressTestParameters] = useState({});
  const [groupedStressTestResults, setGroupedStressTestResults] = useState([]);
  const [insuranceScenario, setInsuranceScenario] = useState(null);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const { globalState } = useContext(AppContext);
  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);
  const [isLoading, setIsLoading] = useState(true);
  const [isResultsLoading, setIsResultsLoading] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState(() => {
    const initialExpanded = new Set();
    return initialExpanded;
  });
  const handleStressTestMenuClick = (_stressTestDisplayField, popupState) => {
    setStressTestDisplayField(_stressTestDisplayField);
    if (popupState?.close) {
      popupState.close();
    }
  };

  const handleStressTestOptionsDialogOpenClick = async (_practice, _group) => {
    setSelectedPractice({ ..._practice, county_name: _group.county_name, state_name: _group.state_name });
    const key = generateStressTestParamsKey({
      county_name: _group.county_name,
      state_name: _group.state_name,
      commodity_name: _practice.commodity_name,
      type_name: _practice.type_name,
      practice_name: _practice.practice_name,
    });
    if (!stressTestParameters[key]) {
      const parameters = await loadStressTestParameters(_practice);
      if (parameters) {
        setStressTestParameters((prev) => ({
          ...prev,
          [key]: parameters,
        }));
      }
    }

    setIsStressTestOptionsDialogOpen(true);
  };

  const handleStressTestParametersChange = async (parameters) => {
    if (!parameters?.id) {
      parameters.id = uuidv4();
      parameters.is_active = true;
    }

    const key = generateStressTestParamsKey({
      county_name: selectedPractice.county_name,
      state_name: selectedPractice.state_name,
      commodity_name: selectedPractice.commodity_name,
      type_name: selectedPractice.type_name,
      practice_name: selectedPractice.practice_name,
    });
    const updatedParameters = {
      ...stressTestParameters,
      [key]: parameters,
    };

    setStressTestParameters(updatedParameters);
    await UsersApiHelper.updateInsuranceStressTestParameters(parameters, globalState);
  };

  const handleInsuranceServiceError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || err, { preventDuplicate: true, variant: "error" });
  };

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };
  const { state: insuranceScenarios, actions: insuranceScenarioActions } = useCardController();

  const loadInsuranceScenarios = async () => {
    try {
      setIsLoading(true);

      const producerFilter = {
        producer_token: globalState.producer_token,
        crop_year: currentYear,
        status: "active",
        is_active: true,
      };
      const _insuranceScenarios = (
        await UsersApiHelper.users_selectObject("InsuranceScenario", producerFilter, globalState)
      )?.sort((a, b) => a.name?.localeCompare(b.name));

      if (_insuranceScenarios?.length) {
        const _InsuranceService = new InsuranceService({
          insuranceScenarios: _insuranceScenarios,
          crop_year: currentYear,
          onError: handleInsuranceServiceError,
          apiConfig: globalState,
        });
        const _insuranceScenarioPopulated = await _InsuranceService.actions.loadInsuranceScenario();
        if (_insuranceScenarioPopulated?.length) {
          insuranceScenarioActions.replace(_insuranceScenarioPopulated);
        } else {
          insuranceScenarioActions.replace([]);
          setIsLoading(false);
        }

        const _loadPremiums = async () => {
          for (var ii = 0; ii < _insuranceScenarioPopulated?.length; ii++) {
            const _currentInsuranceScenario = _insuranceScenarioPopulated[ii];

            const calculatePremiumsResponseInsuranceScenario =
              await _InsuranceService.actions.calculatePremiums(_currentInsuranceScenario);
            _insuranceScenarioPopulated[ii] = calculatePremiumsResponseInsuranceScenario;
          }

          _insuranceScenarioPopulated.forEach((_scenario) => {
            UsersApiHelper.updateInsurancePlans({ InsurancePlans: _scenario?.totals?.insurancePlans }, globalState);
          });
        };

        if (_insuranceScenarioPopulated?.length) {
          _loadPremiums()
            .then(() => {
              setIsLoading(false);
              insuranceScenarioActions.replace(_insuranceScenarioPopulated);
            })
            .catch((err) => {
              console.warn(err);
              enqueueSnackbar(err.detail, { preventDuplicate: true, variant: "error" });
            });
        } else {
          insuranceScenarioActions.replace([]);
          setIsLoading(false);
        }
      } else {
        insuranceScenarioActions.replace([]);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      insuranceScenarioActions.replace([]);
      handleError(err);
    }
  };
  useEffect(() => {
    if (currentYear && globalState?.producer_token) {
      loadInsuranceScenarios();
    }
  }, [currentYear, globalState?.producer_token]);

  useEffect(() => {
    setGroupedStressTestResults([]);
    setInsuranceScenario(null);
    setIsResultsLoading(false);
  }, [currentYear]);

  useEffect(() => {
    if (insuranceScenarios?.length) {
      setInsuranceScenario(insuranceScenarios[0]);
    }
  }, [insuranceScenarios]);

  const loadCountyData = async (budgetCountyPractice) => {
    if (!budgetCountyPractice) return;

    try {
      let _currentCounty = {
        reinsurance_year: budgetCountyPractice.year,
        state_name: budgetCountyPractice.state_name,
        county_name: budgetCountyPractice.county_name,
        commodity_name: budgetCountyPractice.commodity_name,
        type_name: budgetCountyPractice.type_name,
        practice_name: budgetCountyPractice.practice_name,
      };

      let currentPrices = await RmaApiHelper.selectPrices(_currentCounty);

      if (!currentPrices?.length) {
        currentPrices = await RmaApiHelper.selectPrices({
          ..._currentCounty,
          reinsurance_year: budgetCountyPractice.year - 1,
        });

        if (currentPrices.length) {
          currentPrices[0] = {
            ...currentPrices[0],
            projected_price: budgetCountyPractice?.projected_price,
            is_prior_year: true,
          };
        }
      }

      const currentCountyData = await RmaApiHelper.selectCountyData(_currentCounty);
      const currentCounty = {
        ...(_currentCounty || {}),
        ...(currentPrices.length ? currentPrices[0] : {}),
        reinsurance_year: budgetCountyPractice.year,
        expected_index_value: currentCountyData?.length ? currentCountyData[0].expected_index_value : null,
      };
      return currentCounty;
    } catch (err) {
      console.error("Error loading county data:", err);
      return null;
    }
  };
  const getBudgetCountyPractice = async (budgetCountyPractice) => {
    const _activeBudgetCountyPractice = { ...budgetCountyPractice };

    _activeBudgetCountyPractice.reinsurance_year = _activeBudgetCountyPractice?.year;
    _activeBudgetCountyPractice.production_cost_per_acre =
      _activeBudgetCountyPractice?.reported_acreage > 0
        ? _activeBudgetCountyPractice?.production_cost / _activeBudgetCountyPractice?.reported_acreage
        : 0;

    const _cropFilter = {
      crop_year: insuranceScenario.crop_year,
      location_state_name: budgetCountyPractice.state_name,
      location_county_name: budgetCountyPractice.county_name,
      commodity_name: budgetCountyPractice.commodity_name,
      type_name: budgetCountyPractice.type_name,
      practice_name: budgetCountyPractice.practice_name,
      is_active: true,
    };

    const _crops = await UsersApiHelper.selectProducerFarmFieldCrops(_cropFilter, globalState);
    const _cropsCountyPractice = getUniqueCounties(_crops);
    if (_cropsCountyPractice?.length) {
      const _currentCropCountyPractice = _cropsCountyPractice[0];

      Object.assign(_activeBudgetCountyPractice, {
        approved_yield: _currentCropCountyPractice.approved_yield_weighted,
        rate_yield: _currentCropCountyPractice.rate_yield_weighted,
        adjusted_yield: _currentCropCountyPractice.adjusted_yield_weighted,
        share_percent: _currentCropCountyPractice.share_percent_weighted,
      });
    }
    return _activeBudgetCountyPractice;
  };

  const loadStressTestParameters = async (practice) => {
    if (!globalState?.producer_token || !practice) return null;
    try {
      const filter = {
        producer_token: globalState.producer_token,
        insurance_scenario_id: insuranceScenario?.id,
        state_name: practice.state_name,
        county_name: practice.county_name,
        commodity_name: practice.commodity_name,
        type_name: practice.type_name,
        practice_name: practice.practice_name,
        is_active: true,
      };
      const _stressTestParameters = await UsersApiHelper.users_selectObject(
        "InsuranceStressTestParameters",
        filter,
        globalState
      );

      return _stressTestParameters?.length ? _stressTestParameters[0] : null;
    } catch (err) {
      handleError(err);
      return null;
    }
  };

  const processPractice = async (practice) => {
    const countyData = await loadCountyData(practice);
    const _budgetCountyPractice = await getBudgetCountyPractice(practice);

    const key = generateStressTestParamsKey({
      county_name: practice.county_name,
      state_name: practice.state_name,
      commodity_name: practice.commodity_name,
      type_name: practice.type_name,
      practice_name: practice.practice_name,
    });
    let currentParameters = stressTestParameters[key];
    if (!currentParameters) {
      currentParameters = await loadStressTestParameters(practice);
      if (currentParameters) {
        setStressTestParameters((prev) => ({
          ...prev,
          [key]: currentParameters,
        }));
      }
    }

    const props = {
      farmParameters: _budgetCountyPractice,
      countyParameters: countyData,
      insurancePlanParameters: practice?.insurancePlans,
      testParameters: currentParameters,
      apiHelper: ApiHelper,
    };

    const _currentStressTestResults = await calculateStressTest(props);

    if (_currentStressTestResults?.results?.[0]?.length) {
      _currentStressTestResults.results.forEach((row) => {
        row.forEach((cell) => {
          cell.projected_revenue = (cell.revenue_simple || 0) * (cell.reported_acreage || 0);
          cell.income_before_insurance = (cell.net_profit_simple || 0) * (cell.reported_acreage || 0);
          cell.net_income_after_insurance = (cell.net_profit_insured || 0) * (cell.reported_acreage || 0);
          cell.income_per_acre_before_insurance = cell.net_profit_simple || 0;
          cell.income_per_acre_with_insurance = cell.net_profit_insured || 0;
        });
      });

      _currentStressTestResults.cecl = calculate_cecl_from_stresstest({
        stressTestResults: _currentStressTestResults.results,
      });

      return {
        practice,
        stressTestResults: _currentStressTestResults,
        countyData,
        budgetCountyPractice: _budgetCountyPractice,
      };
    }
    return null;
  };

  const loadStressTestResults = async () => {
    try {
      setIsResultsLoading(true);
      if (!insuranceScenario?.budgetCountyPractices?.length) {
        setIsResultsLoading(false);
        return;
      }

      const processedPractices = await Promise.all(insuranceScenario.budgetCountyPractices.map(processPractice));

      const results = {};
      processedPractices.filter(Boolean).forEach(({ practice, stressTestResults, budgetCountyPractice }) => {
        const groupKey = `${practice.county_name}-${practice.state_name}`;
        if (!results[groupKey]) {
          results[groupKey] = {
            county_name: practice.county_name,
            state_name: practice.state_name,
            practices: [],
          };
        }
        const countyData = insuranceScenario?.budgetCountyPractices?.find(
          (p) =>
            p.county_name === practice.county_name &&
            p.state_name === practice.state_name &&
            p.commodity_name === practice.commodity_name
        );
        results[groupKey].practices.push({
          commodity_name: practice.commodity_name,
          type_name: practice.type_name,
          practice_name: practice.practice_name,
          stressTestResults,
          countyData: countyData?.countyData,
          budgetCountyPractice,
        });
      });

      setGroupedStressTestResults(
        Object.values(results).sort((a, b) => {
          if (a.county_name !== b.county_name) {
            return a.county_name.localeCompare(b.county_name);
          }
          return a.state_name.localeCompare(b.state_name);
        })
      );
    } catch (err) {
      setGroupedStressTestResults([]);
      handleError(err);
    } finally {
      setIsResultsLoading(false);
    }
  };
  useEffect(() => {
    if (globalState?.producer_token && insuranceScenario?.budgetCountyPractices?.length) {
      loadStressTestResults();
    } else if (globalState?.producer_token && insuranceScenario && !insuranceScenario?.budgetCountyPractices?.length) {
      setIsResultsLoading(false);
    }
  }, [insuranceScenario, globalState?.producer_token, stressTestParameters]);

  useEffect(() => {
    if (groupedStressTestResults?.length) {
      const initialExpanded = new Set();
      setExpandedAccordions(initialExpanded);
    }
  }, [groupedStressTestResults]);

  const calculateEnterpriseStressTest = (stressTestResults) => {
    const rows = stressTestResults[0]?.results?.length || 10;
    const cols = stressTestResults[0]?.results[0]?.length || 10;
    const enterpriseGrid = Array(rows)
      .fill()
      .map(() =>
        Array(cols)
          .fill()
          .map(() => ({
            price: 0,
            yield: 0,
            net_profit_simple: 0,
            net_profit_insured: 0,
            indemnity_total_per_acre: 0,
            net_insurance_benefit_per_acre: 0,
            net_insurance_benefit: 0,
            indemnityResults: [],
            income_before_insurance: 0,
            net_income_after_insurance: 0,
            revenue_insured: 0, // This will be weighted average per acre
            revenue_simple: 0,
            projected_revenue: 0,
            income_per_acre_before_insurance: 0,
            income_per_acre_with_insurance: 0,
            reported_acreage: 0, // Track total acreage for weighted averages
          }))
      );

    for (let i = 0; i < 10; i++) {
      for (let j = 0; j < 10; j++) {
        enterpriseGrid[i][j].price = i + 1;
        enterpriseGrid[i][j].yield = j + 1;
      }
    }

    stressTestResults.forEach((cropStressTest) => {
      cropStressTest.results.forEach((row, i) => {
        row.forEach((cell, j) => {
          if (i < 10 && j < 10) {
            const acreage = cell.reported_acreage || 0;

            // Sum TOTAL values (these will be displayed as-is for total metrics)
            enterpriseGrid[i][j].net_insurance_benefit += cell.net_insurance_benefit || 0;
            enterpriseGrid[i][j].income_before_insurance += (cell.net_profit_simple || 0) * acreage;
            enterpriseGrid[i][j].net_income_after_insurance += (cell.net_profit_insured || 0) * acreage;
            enterpriseGrid[i][j].projected_revenue += (cell.revenue_simple || 0) * acreage;

            // Accumulate weighted sums for per-acre metrics (will divide by total acreage later)
            enterpriseGrid[i][j].indemnity_total_per_acre += (cell.indemnity_total_per_acre || 0) * acreage;
            enterpriseGrid[i][j].net_insurance_benefit_per_acre += (cell.net_insurance_benefit_per_acre || 0) * acreage;

            // Track total acreage
            enterpriseGrid[i][j].reported_acreage += acreage;

            if (cell.indemnityResults) {
              enterpriseGrid[i][j].indemnityResults = [
                ...enterpriseGrid[i][j].indemnityResults,
                ...cell.indemnityResults,
              ];
            }
          }
        });
      });
    });

    // Calculate weighted averages for per-acre metrics
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        const totalAcreage = enterpriseGrid[i][j].reported_acreage;
        if (totalAcreage > 0) {
          // Calculate per-acre metrics by dividing totals by total acreage
          enterpriseGrid[i][j].revenue_insured = enterpriseGrid[i][j].projected_revenue / totalAcreage;
          enterpriseGrid[i][j].revenue_simple = enterpriseGrid[i][j].projected_revenue / totalAcreage;

          enterpriseGrid[i][j].income_per_acre_before_insurance =
            enterpriseGrid[i][j].income_before_insurance / totalAcreage;
          enterpriseGrid[i][j].income_per_acre_with_insurance =
            enterpriseGrid[i][j].net_income_after_insurance / totalAcreage;
          enterpriseGrid[i][j].net_profit_simple = enterpriseGrid[i][j].income_before_insurance / totalAcreage;
          enterpriseGrid[i][j].net_profit_insured = enterpriseGrid[i][j].net_income_after_insurance / totalAcreage;

          // Divide accumulated weighted sums by total acreage
          enterpriseGrid[i][j].indemnity_total_per_acre /= totalAcreage;
          enterpriseGrid[i][j].net_insurance_benefit_per_acre /= totalAcreage;
        }
      }
    }

    return {
      results: enterpriseGrid,
      insurancePlans: stressTestResults[0]?.insurancePlans || [],
    };
  };

  const totalResults = useMemo(() => {
    const _totalResults = groupedStressTestResults
      .map((group) => {
        return group.practices.map((practice) => {
          return practice.stressTestResults;
        });
      })
      .flat();
    return calculateEnterpriseStressTest(_totalResults).results;
  }, [groupedStressTestResults]);

  const maxLossValue = useMemo(() => {
    if (!totalResults?.length) return -1000;

    let minValue = 0;
    totalResults.forEach((row) => {
      row.forEach((cell) => {
        const value = cell[stressTestDisplayField];
        if (value < minValue) {
          minValue = value;
        }
      });
    });

    // Return the minimum value or -1000 as fallback
    return minValue < 0 ? minValue : -1000;
  }, [totalResults, stressTestDisplayField]);

  const formMaxWidth = "1200px";

  const handleAccordionChange = (groupIndex, practiceIndex) => (event, isExpanded) => {
    const accordionKey = `${groupIndex}-${practiceIndex}`;
    setExpandedAccordions((prev) => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(accordionKey);
      } else {
        newSet.delete(accordionKey);
      }
      return newSet;
    });
  };

  return (
    <>
      <WorkspaceHeader
        title="Stress Tests"
        category={{
          title: "Reports",
          icon: <ReportsIcon />,
        }}
      />
      <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }} className="only-print">
        {`as of ${currentDate}`}
      </Typography>

      <Box className="no-print">
        <WorkspacePageHeader
          currentYear={currentYear}
          onYearChange={setCurrentYear}
          isLoading={isLoading || isResultsLoading}
          additionalControls={
            <PopupState variant="popover" popupId="stress-test-popup-menu">
              {(popupState) => (
                <>
                  <Button variant="outlined" {...bindTrigger(popupState)}>
                    {calculateStressTestDisplayFieldLabel(stressTestDisplayField)} <ArrowDropDownOutlined />
                  </Button>
                  <Menu {...bindMenu(popupState)}>
                    {menuOptions.map((option, index) => {
                      if (option === "divider") {
                        return <Divider key={`divider-${index}`} />;
                      }
                      return (
                        <MenuItem key={option} onClick={() => handleStressTestMenuClick(option, popupState)}>
                          {stressTestDisplayField === option ? (
                            <CheckOutlined sx={{ mr: 1 }} />
                          ) : (
                            <Icon sx={{ mr: 1 }} />
                          )}
                          {calculateStressTestDisplayFieldLabel(option)}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </>
              )}
            </PopupState>
          }
        />
      </Box>
      <Box
        className="only-print"
        sx={{ fontSize: "0.85rem", lineHeight: "1.2", marginBottom: "0.5rem", marginTop: "0.5rem" }}
      >
        {currentYear} - {calculateStressTestDisplayFieldLabel(stressTestDisplayField)}
      </Box>

      <Divider sx={{ mb: 4 }} className="no-print" />

      <Box sx={{ maxWidth: formMaxWidth }}>
        {isLoading || isResultsLoading ? (
          <>
            <Stack direction="row" spacing={2} sx={{ alignItems: "center", gap: 2 }} className="headerWithMenuButton">
              <Skeleton variant="text" width={320} height={40} />
            </Stack>
            <Box>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {Array(10)
                      .fill(0)
                      .map((_, i) => (
                        <TableRow key={i}>
                          {Array(10)
                            .fill(0)
                            .map((_, j) => (
                              <TableCell key={j}>
                                <Skeleton animation="wave" />
                              </TableCell>
                            ))}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        ) : !groupedStressTestResults?.length ? (
          <EmptyCard
            text="No stress test results found"
            secondaryText="Try adjusting your filters to see if any results match your criteria."
          />
        ) : (
          <>
            <Box
              sx={{
                maxWidth: formMaxWidth,
                margin: "0 auto",
                mb: 4,
                "@media print": {
                  breakInside: "avoid",
                  pageBreakInside: "avoid",
                },
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  "@media print": {
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                  },
                }}
              >
                Enterprise Stress Test
              </Typography>
              <Box
                sx={{
                  "@media print": {
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                  },
                }}
              >
                <InsuranceOptimizeDataGrid
                  commodity_name="all"
                  displayFieldName={stressTestDisplayField}
                  yAxisFieldName={"price"}
                  stressTestResults={totalResults}
                  formatYAxisAsDollars={false}
                  doMarkNegativeValues={true}
                  maxLossValue={maxLossValue}
                  isShowAxisLabels={false}
                />
              </Box>
            </Box>
            {groupedStressTestResults.map((group, groupIndex) => (
              <Box
                key={groupIndex}
                sx={{
                  mb: 4,
                  "@media print": {
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                  },
                }}
              >
                <Divider />
                <Typography variant="h1">{`${group.county_name}, ${group.state_name}`}</Typography>

                {group.practices.map((practice, practiceIndex) => (
                  <Accordion
                    key={practiceIndex}
                    defaultExpanded={false}
                    onChange={handleAccordionChange(groupIndex, practiceIndex)}
                    className="stress-test-accordion"
                    sx={{
                      "@media print": {
                        breakInside: "avoid",
                        pageBreakInside: "avoid",
                        "& .MuiCollapse-root": {
                          height: "auto !important",
                          visibility: "visible !important",
                        },
                        "& .MuiAccordionDetails-root": {
                          display: "block !important",
                          padding: "16px !important",
                        },
                        "& .MuiAccordionSummary-expandIconWrapper": {
                          display: "none",
                        },
                      },
                    }}
                  >
                    <AccordionSummary>
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                          paddingX: 2,
                          justifyContent: "start",
                          alignItems: "center",
                          gap: 2,
                          width: "100%",
                          marginBottom: expandedAccordions.has(`${groupIndex}-${practiceIndex}`) ? 4 : 0,
                        }}
                        className="headerWithMenuButton"
                      >
                        <ArrowDownIcon
                          sx={{
                            width: 24,
                            height: 24,
                            transform: expandedAccordions.has(`${groupIndex}-${practiceIndex}`)
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.3s ease",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: { xs: "18px", sm: "24px" },
                          }}
                        >
                          {practice.commodity_name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: "18px", sm: "24px" },
                          }}
                        >
                          {practice.practice_name}
                        </Typography>
                        {expandedAccordions.has(`${groupIndex}-${practiceIndex}`) && (
                          <Button
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStressTestOptionsDialogOpenClick(practice, group);
                            }}
                            sx={{
                              "@media print": {
                                display: "none !important",
                              },
                            }}
                          >
                            <CalculateOutlined sx={{ mr: 1 }} /> Options
                          </Button>
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          "@media print": {
                            breakInside: "avoid",
                            pageBreakInside: "avoid",
                          },
                        }}
                      >
                        <InsuranceOptimizeDataGrid
                          commodity_name={practice.commodity_name}
                          displayFieldName={stressTestDisplayField}
                          yAxisFieldName={"price"}
                          yAxisDisplayName={"Price"}
                          stressTestResults={practice.stressTestResults?.results}
                          doMarkNegativeValues={true}
                          maxLossValue={
                            totalValueFields.includes(stressTestDisplayField)
                              ? -1 * practice.budgetCountyPractice?.production_cost
                              : -1 * practice.budgetCountyPractice?.production_cost_per_acre
                          }
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ))}
          </>
        )}
      </Box>

      <Dialog open={isStressTestOptionsDialogOpen}>
        <InsuranceStressTestParametersDialog
          onClose={() => setIsStressTestOptionsDialogOpen(false)}
          onChange={handleStressTestParametersChange}
          initialStressTestParameters={
            selectedPractice
              ? stressTestParameters[
                  generateStressTestParamsKey({
                    county_name: selectedPractice.county_name,
                    state_name: selectedPractice.state_name,
                    commodity_name: selectedPractice.commodity_name,
                    type_name: selectedPractice.type_name,
                    practice_name: selectedPractice.practice_name,
                  })
                ]
              : null
          }
          budgetCountyPractice={selectedPractice?.budgetCountyPractice}
          countyData={selectedPractice?.countyData}
        />
      </Dialog>
    </>
  );
}
